import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthCompleteView from "../components/AuthCompleteView";
import { useAuth } from "../contexts/AuthContext";
import { useOAuthCompletion } from "../hooks/useOAuthCompletion";
import type { ClientSession } from "../types/auth";

/**
 * AddDevicePage
 *
 * Shows the AuthCompleteView after MCP connector OAuth completes.
 * Users can see their devices and manually click "Connect to Remote Desktop Commander"
 * to complete the OAuth flow.
 */
export default function AddDevicePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, accessToken, loading, signOut } = useAuth();

    // Get params from URL
    const sessionId = searchParams.get("session_id");
    // Which assistant started this run, set by the server from the OAuth redirect host.
    const client = searchParams.get("client");

    // Session construction
    const session: ClientSession | null =
        user && accessToken
            ? {
                  user,
                  access_token: accessToken,
              }
            : null;

    // Use OAuth completion hook
    const oauthCompletion = useOAuthCompletion(sessionId);

    // If no session_id, redirect to login
    useEffect(() => {
        if (!loading && !sessionId) {
            console.log("⚠️ [ADD-DEVICE] No session_id found, redirecting to login");
            navigate("/");
        }
    }, [
        loading,
        sessionId,
        navigate,
    ]);

    // Show loading while auth is loading
    if (loading || !session) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100vh",
                    fontFamily: "system-ui, sans-serif",
                }}
            >
                Loading...
            </div>
        );
    }

    // Show AuthCompleteView for MCP connector flow
    return <AuthCompleteView session={session} client={client} redirectWithToken={oauthCompletion.handleComplete} handleSignOut={signOut} />;
}
